import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../supabaseClient';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    businessName: '',
    fundingType: '',
    loanAmount: '',
    message: '',
    agreeToSms: false,
    agreeToTcpa: false
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agreeToTcpa) {
      setStatus({
        type: 'error',
        message: 'Please agree to the TCPA consent to continue.'
      });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const { error } = await supabase
        .from('miranda_contacts')
        .insert([{
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          business_name: formData.businessName,
          funding_type: formData.fundingType,
          loan_amount: formData.loanAmount,
          message: formData.message,
          agree_to_sms: formData.agreeToSms,
          agree_to_tcpa: formData.agreeToTcpa,
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;

      setStatus({
        type: 'success',
        message: 'Thank you! Your consultation request has been received. We will contact you within 24 hours.'
      });

      setFormData({
        fullName: '',
        email: '',
        phone: '',
        businessName: '',
        fundingType: '',
        loanAmount: '',
        message: '',
        agreeToSms: false,
        agreeToTcpa: false
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus({
        type: 'error',
        message: 'There was an error submitting your request. Please try again or call us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - Miranda Landa Services LLC | Schedule Your Consultation</title>
        <meta name="description" content="Contact Miranda Landa Services LLC for professional business capital consulting. Schedule your free consultation today." />
      </Helmet>

      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
        color: 'white',
        padding: '5rem 2rem',
        textAlign: 'center'
      }}>
        <div style={{maxWidth: '800px', margin: '0 auto'}}>
          <h1 style={{fontSize: '3rem', fontWeight: '800', marginBottom: '1.5rem'}}>
            Get Started Today
          </h1>
          <p style={{fontSize: '1.3rem', opacity: 0.95, lineHeight: '1.6'}}>
            Schedule your free consultation with our business capital experts
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section style={{padding: '5rem 2rem', background: 'white'}}>
        <div style={{maxWidth: '800px', margin: '0 auto'}}>
          {/* Contact Info */}
          <div style={{
            background: '#f0fdf4',
            padding: '2rem',
            borderRadius: '12px',
            marginBottom: '3rem',
            textAlign: 'center'
          }}>
            <h2 style={{fontSize: '1.8rem', fontWeight: '700', color: '#064e3b', marginBottom: '1.5rem'}}>
              Contact Information
            </h2>
            <div style={{display: 'grid', gap: '1rem', fontSize: '1.1rem', color: '#065f46'}}>
              <div>
                <strong>📧 General Inquiries:</strong> <a href="mailto:info@mirandaconnects.com" style={{color: '#059669'}}>info@mirandaconnects.com</a>
              </div>
              <div>
                <strong>📧 Support:</strong> <a href="mailto:support@mirandaconnects.com" style={{color: '#059669'}}>support@mirandaconnects.com</a>
              </div>
              <div>
                <strong>⏰ Response Time:</strong> Within 24 hours during business days
              </div>
              <div>
                <strong>🕐 Business Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM EST
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="card">
            <h2 style={{fontSize: '2rem', fontWeight: '700', color: '#064e3b', marginBottom: '1.5rem', textAlign: 'center'}}>
              Request a Consultation
            </h2>

            {status.message && (
              <div style={{
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '2rem',
                background: status.type === 'success' ? '#d1fae5' : '#fee2e2',
                color: status.type === 'success' ? '#065f46' : '#991b1b',
                border: `1px solid ${status.type === 'success' ? '#10b981' : '#ef4444'}`
              }}>
                {status.message}
              </div>
            )}

            <form onSubmit={handleSubmit} style={{display: 'grid', gap: '1.5rem'}}>
              <div>
                <label style={{display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem'}}>
                  Full Name *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                <div>
                  <label style={{display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem'}}>
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem'}}>
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem'}}>
                  Business Name *
                </label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                <div>
                  <label style={{display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem'}}>
                    Funding Type
                  </label>
                  <select
                    name="fundingType"
                    value={formData.fundingType}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select Type</option>
                    <option value="business-loan">Business Loan</option>
                    <option value="line-of-credit">Line of Credit</option>
                    <option value="equipment-financing">Equipment Financing</option>
                    <option value="working-capital">Working Capital</option>
                    <option value="sba-loan">SBA Loan</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label style={{display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem'}}>
                    Estimated Amount
                  </label>
                  <select
                    name="loanAmount"
                    value={formData.loanAmount}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select Range</option>
                    <option value="under-50k">Under $50,000</option>
                    <option value="50k-100k">$50,000 - $100,000</option>
                    <option value="100k-250k">$100,000 - $250,000</option>
                    <option value="250k-500k">$250,000 - $500,000</option>
                    <option value="500k-1m">$500,000 - $1,000,000</option>
                    <option value="over-1m">Over $1,000,000</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem'}}>
                  Tell Us About Your Business
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontFamily: 'inherit'
                  }}
                  placeholder="Please share details about your business, funding needs, and goals..."
                />
              </div>

              {/* TCPA Consent */}
              <div style={{
                background: '#fef3c7',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '2px solid #f59e0b'
              }}>
                <label style={{display: 'flex', gap: '0.75rem', alignItems: 'flex-start', cursor: 'pointer'}}>
                  <input
                    type="checkbox"
                    name="agreeToTcpa"
                    checked={formData.agreeToTcpa}
                    onChange={handleChange}
                    required
                    style={{marginTop: '0.25rem', width: '18px', height: '18px'}}
                  />
                  <span style={{fontSize: '0.95rem', color: '#78350f', lineHeight: '1.6'}}>
                    <strong>Required:</strong> By submitting this form, I agree to receive SMS updates from Miranda Landa Services LLC 
                    regarding my consultation request, funding updates, and financing guidance. I understand that consent is not a 
                    condition of purchase and I may opt out at any time by replying STOP. Message and data rates may apply. 
                    View our <a href="/sms-policy" style={{color: '#059669', textDecoration: 'underline'}}>SMS Policy</a> and{' '}
                    <a href="/privacy" style={{color: '#059669', textDecoration: 'underline'}}>Privacy Policy</a>.
                  </span>
                </label>
              </div>

              {/* Optional SMS */}
              <div style={{
                background: '#f0fdf4',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid #10b981'
              }}>
                <label style={{display: 'flex', gap: '0.75rem', alignItems: 'flex-start', cursor: 'pointer'}}>
                  <input
                    type="checkbox"
                    name="agreeToSms"
                    checked={formData.agreeToSms}
                    onChange={handleChange}
                    style={{marginTop: '0.25rem', width: '18px', height: '18px'}}
                  />
                  <span style={{fontSize: '0.95rem', color: '#065f46', lineHeight: '1.6'}}>
                    <strong>Optional:</strong> I would like to receive additional SMS updates about business financing updates, 
                    funding consultation reminders, and loan option follow-ups. I can opt out anytime by replying STOP. 
                    Msg & data rates may apply.
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  background: isSubmitting ? '#9ca3af' : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  border: 'none',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s'
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Consultation Request'}
              </button>

              <p style={{fontSize: '0.9rem', color: '#6b7280', textAlign: 'center'}}>
                * Required fields
              </p>
            </form>
          </div>

          {/* Disclaimer */}
          <div style={{
            marginTop: '3rem',
            padding: '2rem',
            background: '#f9fafb',
            borderRadius: '12px',
            borderLeft: '4px solid #10b981'
          }}>
            <h3 style={{fontSize: '1.2rem', fontWeight: '700', color: '#064e3b', marginBottom: '1rem'}}>
              Important Information
            </h3>
            <p style={{fontSize: '0.95rem', color: '#4b5563', lineHeight: '1.7'}}>
              Miranda Landa Services LLC is a consulting firm providing business capital advisory services. 
              We are not a lender and do not make lending decisions. All financing is provided by third-party 
              financial institutions. Submission of this form does not guarantee approval or funding. 
              Your information will be kept confidential and used only for consultation purposes.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
